/**
 * Freeze V2 — Mass Embryo Creation Dialog
 *
 * Slaughterhouse sites create temp embryos in bulk by specifying
 * Grade 1 and Grade 2 counts. Replaces v1's PrimeReact ConfirmDialog
 * with Base UI Dialog.
 */
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

type MassEmbryoDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (grade1Count: number, grade2Count: number) => void;
  isLoading?: boolean;
};

export const MassEmbryoDialog = ({
  open,
  onOpenChange,
  onConfirm,
  isLoading = false,
}: MassEmbryoDialogProps) => {
  const [grade1, setGrade1] = useState(0);
  const [grade2, setGrade2] = useState(0);

  const handleConfirm = () => {
    if (grade1 + grade2 === 0) return;
    onConfirm(grade1, grade2);
    setGrade1(0);
    setGrade2(0);
  };

  const handleCancel = () => {
    setGrade1(0);
    setGrade2(0);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="md:max-w-sm">
        <DialogHeader>
          <DialogTitle>Mass Embryo Creation</DialogTitle>
          <DialogDescription>
            Enter the number of Grade 1 and Grade 2 embryos to create.
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-6 py-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="grade1">Grade 1</Label>
            <Input
              id="grade1"
              type="number"
              min={0}
              value={grade1}
              onChange={(e) =>
                setGrade1(Math.max(0, parseInt(e.target.value) || 0))
              }
              className="w-24"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="grade2">Grade 2</Label>
            <Input
              id="grade2"
              type="number"
              min={0}
              value={grade2}
              onChange={(e) =>
                setGrade2(Math.max(0, parseInt(e.target.value) || 0))
              }
              className="w-24"
            />
          </div>
        </div>
        {grade1 + grade2 > 0 && (
          <p className="text-muted-foreground text-sm">
            Total: {grade1 + grade2} embryos will be created
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isLoading || grade1 + grade2 === 0}
          >
            {isLoading && (
              <i className="pi pi-spin pi-spinner mr-1.5 text-xs" />
            )}
            Create Embryos
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
